// Ported from NearpodNext's `src/helpers/math/mathjax.js`, which holds this and mathjax.ts in one
// file. Split here to mirror connection/socketInstance.ts + socketHelper.ts: this is the only
// module that touches `window.MathJax`.
//
// The configuration below must stay identical to NearpodNext's. Formulas are authored there and
// rendered here, so a drift renders the same content differently in creation and in session.

import loadScript from '../loadScript'

// Pinned to an exact patch, not `mathjax@4`: everything below depends on MathJax 4 internals that a
// floating range can change in production with no deploy here — whenReady, startup.promise,
// startup.output.options.matchFontHeight, typesetClear, chtml.linebreaks.inline,
// options.menuOptions.settings.*, and the data-semantic-speech-none attribute mathSpeech.ts reads.
// Re-check all of those against the new release before bumping this.
const MATHJAX_CDN_URL = 'https://cdn.jsdelivr.net/npm/mathjax@4.1.3/tex-mml-chtml.js'

declare global {
	interface Window {
		// Holds the pre-load configuration until MathJax boots and replaces it with the library
		// API. `any` is what keeps both shapes assignable.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		MathJax: any
	}
}

let loadPromise: Promise<void> | null = null

// MathJax reads window.MathJax while it boots, so this has to run before the tag is appended.
const applyPreLoadConfig = () => {
	window.MathJax = {
		tex: {
			// MathLive's formula editor emits these ISO 80000-2 upright-constant macros, which are
			// not standard LaTeX and so need defining explicitly. They are its only non-standard
			// output — \\sqrt, \\frac, \\int and friends are plain TeX that MathJax already knows.
			macros: {
				differentialD: '\\mathrm{d}',
				imaginaryI: '\\mathrm{i}',
				imaginaryJ: '\\mathrm{j}',
				exponentialE: '\\mathrm{e}',
			},
			// The `html` package's \href, \class, \style, \cssId and \data put authored links, classes,
			// ids, inline styles and data attributes on the rendered formula, after the sanitizer has
			// run: a formula's LaTeX is only an attribute value to the sanitizer, and \href accepts any
			// URL scheme, javascript: included. Formulas need none of them, so keep the package out: an
			// empty list stops MathJax from loading it on first use, and the `require` entry stops
			// \require{html}. A formula that uses one of its macros shows the macro name in red,
			// followed by its arguments as plain math. Matches @nearpod-packages/rich-text.
			autoload: {
				html: [],
			},
			require: {
				allow: {
					html: false,
				},
			},
		},
		options: {
			// `enrich` + `speech` are what populate the data-semantic-speech-none attribute that
			// answer aria-labels read out of the typeset DOM.
			menuOptions: {
				settings: {
					enrich: true,
					speech: true,
					braille: true,
					collapsible: false,
					assistiveMml: false,
				},
			},
			a11y: {
				// Make the Explorer's own focus highlight transparent. The visible focus indicator is
				// drawn as a brand-blue outline in App.css — remove one and the other must go too, or
				// keyboard users lose the indicator entirely.
				backgroundOpacity: 0,
			},
		},
		chtml: {
			// TTC renders formulas in single-line, truncating containers (answer chips, question
			// header). MathJax 4's automatic inline linebreaking would split a formula across lines
			// and defeat that truncation.
			linebreaks: {
				inline: false,
			},
		},
		startup: {
			// Never scan the document automatically; typesetting is always explicit per element.
			typeset: false,
		},
	}
}

// Safe to call from anywhere, any number of times: all callers share one network request.
const ensureLoaded = (): Promise<void> => {
	// A rejected loadPromise is intentionally retained: we do not retry the CDN within a page
	// lifetime. Every caller then takes the raw-LaTeX fallback path in mathjax.ts.
	if (loadPromise) return loadPromise
	applyPreLoadConfig()
	loadPromise = loadScript(MATHJAX_CDN_URL, { referrerPolicy: 'no-referrer' }).then(
		() => window.MathJax.startup.promise
	)
	return loadPromise
}

export interface ITypesetOptions {
	// MathJax scales every formula so its x-height matches the surrounding font's, which ties formula
	// size to whichever face is in effect. TTC skins ship display fonts with very different x-heights
	// (Rye's is 0.19em against Bubblegum Sans' 0.41em), and a low one drives the scale under the
	// minScale floor of 0.5, halving the formula. It also makes the result depend on whether the skin
	// webfont loaded before typesetting. Pass false where formula size must not inherit that.
	matchFontHeight?: boolean
}

// The output jax reads these options during typesetting rather than at construction, so they can be
// varied per call. They still live on one shared object, hence the critical section: MathJax queues
// typeset calls, so assigning outside whenReady would let a later caller's value apply to an earlier
// caller's typeset. whenReady runs the callback once pending work has drained.
const typeset = (el: HTMLElement, options: ITypesetOptions = {}): Promise<void> => {
	if (options.matchFontHeight === undefined || typeof window.MathJax.whenReady !== 'function') {
		return window.MathJax.typesetPromise([el])
	}
	return window.MathJax.whenReady(() => {
		const outputOptions = window.MathJax.startup.output.options
		const previous = outputOptions.matchFontHeight
		outputOptions.matchFontHeight = options.matchFontHeight
		return window.MathJax.typesetPromise([el]).finally(() => {
			outputOptions.matchFontHeight = previous
		})
	})
}

// Drops MathJax's internal references to a subtree about to be removed or rewritten. Guarded
// because cleanup can run before the CDN has resolved, when there is nothing to clear.
const clear = (el: HTMLElement) => {
	if (window.MathJax && typeof window.MathJax.typesetClear === 'function') {
		window.MathJax.typesetClear([el])
	}
}

export default {
	ensureLoaded,
	typeset,
	clear,
}
